// FilterForm migrated from Final Form to react-hook-form.
//
// FilterForm is a GENERIC form wrapper/container: it has no own input fields.
// All inputs are supplied by consumers via `children`. The wrapper establishes
// the react-hook-form context through <FormProvider> so child filter inputs can
// read/write form state via useFormContext(); it also owns the two output modes
// (live-edit onChange vs. submit/clear/cancel buttons) exactly as before.

import { Box } from '@mui/material';
import isEqual from 'lodash/isEqual';
import React, { memo, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

const rootSx = {
  outline: 'none',
  padding: '0 8px',
};

const contentWrapperSx = {
  marginBottom: '24px',
};

const buttonsWrapperSx = {
  display: 'flex',
  padding: '0 30px 16px 30px',
};

const clearButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.matterColorAnti,

  margin: '0 auto 0 0',
  padding: 0,

  outline: 'none',
  border: 'none',
  cursor: 'pointer',

  '&:focus, &:hover': {
    color: theme.tokens.matterColor,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

const cancelButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.matterColorAnti,

  margin: 0,
  padding: 0,

  outline: 'none',
  border: 'none',
  cursor: 'pointer',

  marginLeft: '48px',

  '&:focus, &:hover': {
    color: theme.tokens.matterColor,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

const submitButtonSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  color: theme.tokens.marketplaceColor,

  margin: '0 0 0 19px',
  padding: 0,

  outline: 'none',
  border: 'none',
  cursor: 'pointer',

  '&:focus, &:hover': {
    color: theme.tokens.marketplaceColorDark,
    transition: `width ${theme.tokens.transitionStyleButton}`,
  },
});

/**
 * FilterForm is a generic search-filter wrapper with two output modes controlled
 * by `liveEdit`. It renders no inputs of its own — they are passed via `children`.
 *
 * - liveEdit=true: no buttons; fires `onChange(values)` on every dirty change.
 * - liveEdit=false: Clear / Cancel / Submit buttons; fires `onSubmit(values)` on
 *   submit. Cancel resets the form to its initial values and calls `onCancel()`.
 *
 * @param {object} props
 * @param {boolean} [props.liveEdit] - Whether to run in live-edit (onChange) mode.
 * @param {Function|null} [props.onChange] - Called on every dirty change (live-edit mode, required).
 * @param {Function|null} [props.onSubmit] - Called on submit (button mode, required).
 * @param {Function|null} [props.onCancel] - Called on cancel (button mode, required).
 * @param {Function|null} [props.onClear] - Called on clear (button mode, required).
 * @param {object} [props.style] - Inline style for the form element.
 * @param {React.ReactNode} [props.children] - The filter inputs to render.
 */
const FilterFormComponent = ({
  liveEdit = false,
  onChange = null,
  onSubmit = null,
  onCancel = null,
  onClear = null,
  style = {},
  children,
  ...rest
}) => {
  const intl = useIntl();

  if (liveEdit && typeof onChange !== 'function') {
    throw new Error('FilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (
    !liveEdit &&
    !(
      typeof onCancel === 'function' &&
      typeof onClear === 'function' &&
      typeof onSubmit === 'function'
    )
  ) {
    throw new Error(
      'FilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  // `initialValues` and `keepDirtyOnReinitialize` arrive via `...rest` from
  // FilterPlain/FilterPopup; the remaining rest keys (id, contentPlacementOffset,
  // paddingClasses, showAsPopup) are layout-only and do not drive logic here.
  const {
    initialValues,
    keepDirtyOnReinitialize = false,
    // The following are intentionally pulled out of `rest` so they are NOT spread
    // onto the DOM <form> element (they are layout/identity props consumed by the
    // parent containers, not valid form attributes).
    id: _id,
    contentPlacementOffset: _contentPlacementOffset,
    paddingClasses: _paddingClasses,
    showAsPopup: _showAsPopup,
    ...formRest
  } = rest;

  const methods = useForm({
    defaultValues: initialValues || {},
  });

  const { handleSubmit, reset, watch } = methods;

  // Keep the form in sync when `initialValues` change (re-initialize), mirroring
  // Final Form's reinitialize behavior. `keepDirtyOnReinitialize` maps to RHF's
  // `keepDirtyValues` reset option so in-progress user edits are not clobbered.
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    reset(initialValues || {}, { keepDirtyValues: keepDirtyOnReinitialize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, keepDirtyOnReinitialize]);

  // Live-edit mode: fire onChange(values) only when the form is dirty, matching
  // the original FormSpy({ values, dirty }) handler. Not debounced (by design —
  // unlike the sibling PriceFilterForm which debounces 400ms).
  //
  // Dirtiness is computed by comparing the emitted values against initialValues
  // rather than reading RHF's formState.isDirty: inside a `watch` callback the
  // isDirty proxy has not yet been recomputed for the in-flight change, so it
  // would lag a keystroke (and never report true for the first edit). FF's
  // FormSpy reported dirty synchronously with the value change, so value-vs-
  // initial comparison reproduces that behavior exactly and race-free.
  useEffect(() => {
    if (!liveEdit) {
      return undefined;
    }
    const baseline = initialValues || {};
    const subscription = watch((values, { type }) => {
      // `type` is set only for real user-driven changes; this also skips the
      // initial programmatic emission.
      if (type && !isEqual(values, baseline)) {
        onChange(values);
      }
    });
    return () => subscription.unsubscribe();
  }, [liveEdit, watch, initialValues, onChange]);

  // Live-edit filters render no submit button and — by contract, enforced by the
  // guard above — carry no `onSubmit`: their values reach the parent through
  // `onChange`. The browser still submits the <form> when the user presses Enter
  // inside a filter input, which called the null handler and threw
  // (Sentry VANSITE-FRONTEND-4HZ). Enter is a no-op here, the value is already out.
  const onValidSubmit = (values) => {
    if (liveEdit) {
      return;
    }
    onSubmit(values);
  };

  const handleCancel = () => {
    // reset the form back to its initial values, then notify the parent.
    reset(initialValues || {});
    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  const clear = intl.formatMessage({ id: 'FilterForm.clear' });
  const cancel = intl.formatMessage({ id: 'FilterForm.cancel' });
  const submit = intl.formatMessage({ id: 'FilterForm.submit' });

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        {...formRest}
        action="/"
        method="post"
        onSubmit={handleSubmit(onValidSubmit)}
        style={style}
        sx={rootSx}
        tabIndex="0"
      >
        <Box sx={contentWrapperSx}>{children}</Box>

        {liveEdit ? null : (
          <Box sx={buttonsWrapperSx}>
            <Box component="button" onClick={onClear} sx={clearButtonSx} type="button">
              {clear}
            </Box>
            <Box component="button" onClick={handleCancel} sx={cancelButtonSx} type="button">
              {cancel}
            </Box>
            <Box component="button" sx={submitButtonSx} type="submit">
              {submit}
            </Box>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};

FilterFormComponent.displayName = 'FilterForm';

const FilterForm = memo(FilterFormComponent);

export default FilterForm;
